'use client';

import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

interface NodeTypeConfig {
  minNodes: number;
  maxNodes: number;
}

// https://docs.aws.amazon.com/redshift/latest/mgmt/working-with-clusters.html
// ra3.large and ra3.xlplus support single-node (1) and multi-node (2-N).
// ra3.4xlarge and ra3.16xlarge require at least 2 nodes.
const REDSHIFT_NODE_TYPE_CONFIGS: Record<string, NodeTypeConfig> = {
  'ra3.large': { minNodes: 1, maxNodes: 16 },
  'ra3.xlplus': { minNodes: 1, maxNodes: 32 },
  'ra3.4xlarge': { minNodes: 2, maxNodes: 32 },
  'ra3.16xlarge': { minNodes: 2, maxNodes: 128 },
};

const REDSHIFT_NODE_TYPES = Object.keys(REDSHIFT_NODE_TYPE_CONFIGS);

const getNodeCountOptions = (
  nodeType: string | undefined,
  originalNumberOfNodes: number | undefined
): number[] => {
  if (!nodeType) return [];
  const config = REDSHIFT_NODE_TYPE_CONFIGS[nodeType];
  if (!config) return [];

  // When restoring, AWS enforces a minimum based on data volume.
  // We start from the original node count (guaranteed to work) and only
  // go down to the documented minimum if the original is not available.
  const floorMin =
    originalNumberOfNodes && originalNumberOfNodes >= config.minNodes
      ? originalNumberOfNodes
      : config.minNodes;

  const options: number[] = [];
  for (let i = floorMin; i <= config.maxNodes; i++) {
    options.push(i);
  }
  return options;
};

export const RedshiftNodeTypeSelect = ({
  nodeType,
  originalNodeType,
  onChange,
}: {
  nodeType?: string;
  originalNodeType?: string;
  onChange: (nodeType?: string) => void;
}) => {
  const options = originalNodeType
    ? [
        `${originalNodeType} (original)`,
        ...REDSHIFT_NODE_TYPES.filter((t) => t !== originalNodeType),
      ]
    : REDSHIFT_NODE_TYPES;

  const displayValue =
    nodeType === undefined || nodeType === ''
      ? originalNodeType
        ? `${originalNodeType} (original)`
        : ''
      : nodeType;

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Node type</FormLabel>
      <Autocomplete
        clearOnBlur
        size='small'
        value={displayValue}
        onChange={(_event, newValue) => {
          if (!newValue || newValue.endsWith('(original)')) {
            onChange(undefined);
          } else {
            onChange(newValue);
          }
        }}
        getOptionLabel={(option) => option}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Select node type'
            label=''
            InputLabelProps={{ shrink: true }}
          />
        )}
        options={options}
      />
    </FormControl>
  );
};

export const RedshiftNodeCountSelect = ({
  nodeType,
  originalNodeType,
  numberOfNodes,
  originalNumberOfNodes,
  onChange,
}: {
  nodeType?: string;
  originalNodeType?: string;
  numberOfNodes?: number;
  originalNumberOfNodes?: number;
  onChange: (numberOfNodes?: number) => void;
}) => {
  const effectiveNodeType = nodeType || originalNodeType;
  const isNodeTypeChanged = !!nodeType && nodeType !== originalNodeType;

  // When restoring to the same node type, start from original count.
  // When restoring to a different node type, start from doc minimum
  // since the storage layout changes.
  const floorNodeCount = isNodeTypeChanged ? undefined : originalNumberOfNodes;
  const options = getNodeCountOptions(effectiveNodeType, floorNodeCount);
  const isDisabled = options.length === 0;

  const effectiveValue = numberOfNodes ?? originalNumberOfNodes;
  const isOriginal = numberOfNodes === undefined && !!originalNumberOfNodes;
  const showOriginalNotAvailable =
    isNodeTypeChanged &&
    originalNumberOfNodes &&
    !options.includes(originalNumberOfNodes);

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Number of nodes</FormLabel>
      <Select
        size='small'
        disabled={isDisabled}
        displayEmpty
        value={
          effectiveValue && options.includes(effectiveValue)
            ? effectiveValue
            : ''
        }
        onChange={(e) => {
          const val = e.target.value;
          if (val === '') {
            onChange(undefined);
          } else if (
            !isNodeTypeChanged &&
            Number(val) === originalNumberOfNodes
          ) {
            onChange(undefined);
          } else {
            onChange(Number(val));
          }
        }}
        MenuProps={{
          PaperProps: { sx: { maxHeight: '300px' } },
        }}
        renderValue={(selected) => {
          if (!selected && selected !== 0) {
            return (
              <span style={{ color: 'var(--mui-palette-text-disabled)' }}>
                Select
              </span>
            );
          }
          if (isOriginal && !isNodeTypeChanged) {
            return `${selected} (original)`;
          }
          return `${selected}`;
        }}
      >
        {!isNodeTypeChanged &&
          originalNumberOfNodes &&
          options.includes(originalNumberOfNodes) && (
            <MenuItem value={originalNumberOfNodes}>
              {originalNumberOfNodes} (original)
            </MenuItem>
          )}
        {options
          .filter((n) => isNodeTypeChanged || n !== originalNumberOfNodes)
          .map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
      </Select>
      {showOriginalNotAvailable && (
        <FormHelperText>
          Original node count ({originalNumberOfNodes}) is not available for{' '}
          {effectiveNodeType}. Select a compatible value.
        </FormHelperText>
      )}
    </FormControl>
  );
};
